"use client";

import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ServiceComparison() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelsRef,
    isVisible: panelsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const tiers = [{
    number: "01",
    name: "OTA OPTIMIZATION",
    descriptor: "Essential revenue management for independent hotels seeking visibility.",
    featured: false,
    price: "Starting $499",
    features: [{
      included: true,
      text: "OTA profile audit & cleanup"
    }, {
      included: true,
      text: "Dynamic rate strategy setup"
    }, {
      included: true,
      text: "Content & image optimization"
    }, {
      included: true,
      text: "Monthly performance reporting"
    }, {
      included: false,
      text: "Tech stack integration"
    }, {
      included: false,
      text: "Direct booking conversion"
    }, {
      included: false,
      text: "Revenue audit deep-dive"
    }, {
      included: true,
      text: "Email support access"
    }]
  }, {
    number: "02",
    name: "REVENUE GROWTH",
    descriptor: "Comprehensive management to maximize RevPAR and direct bookings.",
    featured: true,
    price: "Starting $1,200",
    features: [{
      included: true,
      text: "Full OTA management & parity"
    }, {
      included: true,
      text: "Tech stack recommendation"
    }, {
      included: true,
      text: "Direct booking strategy"
    }, {
      included: true,
      text: "Competitor rate monitoring"
    }, {
      included: true,
      text: "Revenue forecasting"
    }, {
      included: true,
      text: "Monthly strategy calls"
    }, {
      included: false,
      text: "Full revenue audit"
    }, {
      included: true,
      text: "Priority support access"
    }]
  }, {
    number: "03",
    name: "REVENUE PARTNER",
    descriptor: "Full-service revenue consultancy for high-performing properties.",
    featured: false,
    price: "Custom Quote",
    features: [{
      included: true,
      text: "End-to-end revenue management"
    }, {
      included: true,
      text: "Tech stack implementation"
    }, {
      included: true,
      text: "Direct booking growth engine"
    }, {
      included: true,
      text: "Advanced revenue auditing"
    }, {
      included: true,
      text: "Market positioning strategy"
    }, {
      included: true,
      text: "Full digital marketing sync"
    }, {
      included: true,
      text: "Perimeter revenue intelligence"
    }, {
      included: true,
      text: "Dedicated account manager"
    }]
  }];
  return <section data-section-id="3547" id="servicecomparison" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-lg" style={{
      background: "radial-gradient(circle, hsla(43, 46%, 55%, 0.08) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs tracking-widest text-muted-foreground mb-6 md:mb-8">
              [ 02 / SERVICES ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight uppercase">
              Revenue
              <br />
              <span className="text-secondary">Solutions.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              Tailored revenue management strategies designed to increase your hotel's profitability and market share.
            </p>
          </div>
        </div>

        {}
        <div ref={panelsRef} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 items-stretch transition-all duration-700 ease-out delay-150 ${panelsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {tiers.map((tier, i) => <div data-index={i} key={tier.number} className={`group relative flex flex-col backdrop-blur-sm bg-card shadow-lg transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-xl ${tier.featured ? "border border-secondary lg:-mt-16 lg:mb-0" : "border border-border"}`}>
              {tier.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-secondary px-4 py-1.5">
                    <span className="font-default text-[10px] tracking-widest text-primary-foreground uppercase whitespace-nowrap">
                      [ Recommended ]
                    </span>
                  </div>
                </div>}

              <div className="flex flex-col flex-1 p-8 md:p-10">
                {}
                <div className={`font-default font-bold text-7xl md:text-8xl leading-none tracking-tight ${tier.featured ? "text-secondary" : "text-foreground"}`}>
                  {tier.number}
                </div>

                {}
                <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight uppercase mt-8">
                  {tier.name}
                </h3>

                {}
                <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-3">
                  {tier.descriptor}
                </p>

                {}
                <ul className="mt-10 space-y-4 flex-1">
                  {tier.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                      {feature.included ? <Plus className="size-4 mt-1 shrink-0 text-secondary" strokeWidth={1.5} /> : <Minus className="size-4 mt-1 shrink-0 text-muted-foreground/50" strokeWidth={1.5} />}
                      <span className={`font-default text-base leading-snug ${feature.included ? "text-foreground" : "text-muted-foreground/60 line-through decoration-muted-foreground/30"}`}>
                        {feature.text}
                      </span>
                    </li>)}
                </ul>

                {}
                <div className="mt-10 h-px w-full bg-border" />

                {}
                <div className="mt-8">
                  <p className="font-default text-xs tracking-widest text-muted-foreground uppercase">
                    Investment From
                  </p>
                  <p className="font-default font-bold text-3xl md:text-4xl tracking-tight mt-2">
                    {tier.price}
                  </p>
                </div>

                {}
                <div className="mt-8">
                  {tier.featured ? <Button asChild className="w-full h-12 rounded-md bg-secondary text-primary-foreground hover:bg-secondary/90 font-default text-xs tracking-widest uppercase shadow-md">
                      <Link to="/ContactPage">Inquire</Link>
                    </Button> : <Button asChild variant="outline" className="w-full h-12 rounded-md border-border bg-transparent text-foreground hover:bg-secondary hover:text-primary-foreground font-default text-xs tracking-widest uppercase">
                      <Link to="/ContactPage">Inquire</Link>
                    </Button>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}